import { useEffect, useMemo, useState } from "react";
import { Button } from "@headlessui/react";
import { AdminImportStudent } from "../rosterImportApiSlice";
import { ConfirmStep } from "./ConfirmStep";
import { ResolveStudentStep } from "./ResolveStudentStep";
import { MergeItem } from "./conflictReviewUtils";

interface RosterImportConflictReviewProps {
	studentsWithConflicts: AdminImportStudent[];
	handleResolveConflicts: (resolvedStudents: AdminImportStudent[]) => void;
}

export default function RosterImportConflictReview({
	studentsWithConflicts,
	handleResolveConflicts,
}: RosterImportConflictReviewProps) {
	// 0..(n-1) = student steps, n = confirmation step
	const [activeIndex, setActiveIndex] = useState(0);

	// <row, selected matchId | null(create new)>
	const [selections, setSelections] = useState<Record<number, number | null>>({});

	const totalStudents = studentsWithConflicts.length;
	const isConfirmStep = activeIndex === totalStudents;

	useEffect(() => {
		setActiveIndex((i) => Math.min(Math.max(i, 0), totalStudents));
	}, [totalStudents]);

	const activeStudent = isConfirmStep ? undefined : studentsWithConflicts[activeIndex];

	const resolvedStudents = useMemo(() => {
		return studentsWithConflicts.map((s) => ({
			...s,
			matchingUserId: selections[s.row] ?? null,
			identifications: [],
		}));
	}, [studentsWithConflicts, selections]);

	const { createNew, merges } = useMemo(() => {
		const createNew: AdminImportStudent[] = [];
		const merges: MergeItem[] = [];

		for (const s of studentsWithConflicts) {
			const choice = selections[s.row] ?? null;

			if (choice == null) {
				createNew.push(s);
				continue;
			}

			const match = s.identifications.find((m) => m.id === choice);
			if (match) merges.push({ student: s, match });
		}

		return { createNew, merges };
	}, [studentsWithConflicts, selections]);

	const goNext = () => setActiveIndex((i) => Math.min(i + 1, totalStudents));
	const goBack = () => setActiveIndex((i) => Math.max(i - 1, 0));

	const stepText = isConfirmStep
		? "Review & confirm"
		: `Resolving ${activeIndex + 1} of ${totalStudents}`;

	return (
		<section>
			<h2>Students Requiring Attention</h2>
			<p className="text-sm text-gray-600">
				Create a new user or choose an existing match for each student.
			</p>
			<p className="text-sm text-gray-600 text-right">{stepText}</p>
			<div className="mt-2 rounded-lg border border-gray-200 bg-white p-4">
				{isConfirmStep ? (
					<ConfirmStep
						createNew={createNew}
						merges={merges}
						updateSelectionsWithCreateNew={(row) =>
							setSelections((prev) => ({ ...prev, [row]: null }))
						}
						updateSelectionsWithMerge={(row, matchId) =>
							setSelections((prev) => ({ ...prev, [row]: matchId }))
						}
					/>
				) : (
					<ResolveStudentStep
						student={activeStudent}
						value={selections[activeStudent!.row] ?? null}
						onChange={(matchId) =>
							setSelections((prev) => ({ ...prev, [activeStudent!.row]: matchId }))
						}
					/>
				)}

				<div className="mt-4 flex items-center justify-between">
					<Button
						onClick={goBack}
						disabled={activeIndex === 0}
						className="btn btn-neutral">
						Back
					</Button>
					{isConfirmStep ? (
						<Button
							type="button"
							className="btn btn-primary"
							onClick={() => handleResolveConflicts(resolvedStudents)}>
							Continue Import
						</Button>
					) : (
						<Button onClick={goNext} className="btn btn-primary">
							{activeIndex === totalStudents - 1 ? "Review" : "Next"}
						</Button>
					)}
				</div>
			</div>
		</section>
	);
}
